import { LngLatBounds } from 'mapbox-gl'

/**
 * Client-side GPX → GeoJSON parser for the Comrades route.
 *
 * Design choice (PRD §15 q5, resolved): parse the committed GPX at runtime in
 * the browser instead of converting to GeoJSON at build time. Reasons:
 *   1. Zero new build tooling; matches what `ComradesDemoPage` already does.
 *   2. The file is small (~210 KB, ~6k trackpoints) and only fetched on first
 *      Map-mode render. Budget: < 200 ms on a mid-tier mobile.
 *   3. Stream A's real `/viewer` endpoint exposes `route.gpx_url` so the
 *      client path is the contract.
 */

export interface RoutePoint {
  latitude: number
  longitude: number
  elevation?: number
  distanceFromStart: number
}

export interface ParsedGpx {
  points: RoutePoint[]
  totalDistanceKm: number
  bounds: LngLatBounds | null
  lineFeature: GeoJSON.Feature<GeoJSON.LineString>
}

export function parseGpx(xmlText: string): ParsedGpx {
  const parser = new DOMParser()
  const xml = parser.parseFromString(xmlText, 'application/xml')
  const trkpts = Array.from(xml.getElementsByTagName('trkpt'))

  const rawPoints = trkpts
    .map((node) => ({
      latitude: Number(node.getAttribute('lat')),
      longitude: Number(node.getAttribute('lon')),
      elevation: Number(node.getElementsByTagName('ele')[0]?.textContent ?? '0'),
    }))
    .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude))

  const bounds = new LngLatBounds()
  let totalMeters = 0
  const points: RoutePoint[] = rawPoints.map((point, index) => {
    if (index > 0) totalMeters += haversineMeters(rawPoints[index - 1], point)
    bounds.extend([point.longitude, point.latitude])
    return { ...point, distanceFromStart: totalMeters }
  })

  return {
    points,
    totalDistanceKm: totalMeters / 1000,
    bounds: points.length ? bounds : null,
    lineFeature: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: points.map((p) => [p.longitude, p.latitude]),
      },
    },
  }
}

export function haversineMeters(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
) {
  const R = 6371000
  const dLat = toRad(b.latitude - a.latitude)
  const dLon = toRad(b.longitude - a.longitude)
  const lat1 = toRad(a.latitude)
  const lat2 = toRad(b.latitude)
  const sinDLat = Math.sin(dLat / 2)
  const sinDLon = Math.sin(dLon / 2)
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon
  return 2 * R * Math.asin(Math.sqrt(h))
}

function toRad(value: number) {
  return value * (Math.PI / 180)
}

/**
 * Returns a position along the parsed route at the given `progress` in [0, 1].
 * Used for placing demo cameras on the map when no real telemetry or payload
 * location is available yet.
 */
export function interpolateRoute(points: RoutePoint[], progress: number) {
  if (points.length === 0) {
    return { latitude: -29.85, longitude: 30.97, heading: 0, distanceKm: 0 }
  }
  const clamped = Math.max(0, Math.min(progress, 0.999))
  const total = points[points.length - 1]?.distanceFromStart ?? 0
  const target = total * clamped
  let next = points.findIndex((p) => p.distanceFromStart >= target)
  if (next <= 0) next = 1
  const prev = points[next - 1] ?? points[0]
  const nxt = points[next] ?? points[points.length - 1]
  const segMeters = Math.max(nxt.distanceFromStart - prev.distanceFromStart, 1)
  const segProgress = (target - prev.distanceFromStart) / segMeters
  return {
    latitude: lerp(prev.latitude, nxt.latitude, segProgress),
    longitude: lerp(prev.longitude, nxt.longitude, segProgress),
    heading: bearingDegrees(prev, nxt),
    distanceKm: target / 1000,
  }
}

/** Nearest-point distance along the route for a given lat/lon. */
export function nearestDistanceKm(points: RoutePoint[], latitude: number, longitude: number) {
  let best = Number.POSITIVE_INFINITY
  let km = 0
  for (const point of points) {
    const d = haversineMeters({ latitude, longitude }, point)
    if (d < best) {
      best = d
      km = point.distanceFromStart / 1000
    }
  }
  return km
}

export function bearingDegrees(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
) {
  const y = Math.sin(toRad(b.longitude - a.longitude)) * Math.cos(toRad(b.latitude))
  const x =
    Math.cos(toRad(a.latitude)) * Math.sin(toRad(b.latitude)) -
    Math.sin(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) *
      Math.cos(toRad(b.longitude - a.longitude))
  const bearing = Math.atan2(y, x) * (180 / Math.PI)
  return (bearing + 360) % 360
}

function lerp(start: number, end: number, t: number) {
  return start + (end - start) * t
}
